import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TwoFactorVerificationResponse, UserSettingsService } from '../../../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedModule } from '../../../../../shared/shared.module';
import { OtpCodeInputComponent } from '../../../../../v2-layout/shared/otp-code-input/otp-code-input.component';

export interface ConfirmTwoFactorInput
{
    type: 'google' | 'sms' | null;
    userId: number;
    canGoBack?: boolean;
    showTrustDevice?: boolean;
}

export interface ConfirmTwoFactorOutput
{
    success: boolean;
    type?: 'google' | 'sms';
    token?: string;
    trustDevice?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-confirm-2fa-code',
    templateUrl: './confirm-2fa-code.component.html',
    styleUrls: ['./confirm-2fa-code.component.scss'],
    standalone: true,
    imports: [
        SharedModule,
        OtpCodeInputComponent,
    ],
})
export class Confirm2faCodeComponent implements OnInit, OnDestroy
{
    @ViewChild(OtpCodeInputComponent) private _otp: OtpCodeInputComponent;

    isSubmitting = false;
    isSuccess = false;
    type: 'google' | 'sms' | null = null;
    canGoBack = true;
    isSendingSms = false;
    showTrustDevice = false;
    trustDevice = false;
    hasError = false;

    /** Current entry from the shared OTP control (mirrors its valueChange). */
    code = '';

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private _userSettingsService: UserSettingsService,
        private _growler: GrowlerService,
        private _dialogRef: MatDialogRef<Confirm2faCodeComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConfirmTwoFactorInput,
    ) {
    }

    ngOnInit(): void {
        this.type = this.data.type;

        if (this.type === 'sms') {
            this.setSmsType();
        }

        if (this.data.hasOwnProperty('canGoBack')) {
            this.canGoBack = this.data.canGoBack;
        }

        this.showTrustDevice = !!this.data.showTrustDevice;
    }

    setSmsType() {
        this.isSendingSms = true;
        this._userSettingsService.sendSmsVerificationCode(this.data.userId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isSendingSms = false;
                    this.type = 'sms';
                    setTimeout(() => this._otp?.focusFirst());
                },
                error: () => {
                    this._growler.error('Error', 'We encountered an error while trying to send the code.');
                    this.isSendingSms = false;
                },
            });
    }

    setGoogleType() {
        this.type = 'google';
        setTimeout(() => this._otp?.focusFirst());
    }

    /** Return to the type-selection screen and clear any partial entry. */
    goBack() {
        this.type = null;
        this.code = '';
    }

    get subtitle(): string {
        if (this.type === 'sms') {
            return 'Enter the 6-digit code sent to your device';
        }
        return 'Open your authenticator app to get your code';
    }

    onCodeChange(code: string): void {
        this.code = code;
    }

    submit(code: string = this.code) {
        if (code.length !== 6) {
            return;
        }

        this.isSubmitting = true;

        const onSuccess = (response: TwoFactorVerificationResponse) => {
            this.isSubmitting = false;
            this.isSuccess = true;
            this._successTimer = setTimeout(() => {
                this._dialogRef.close(<ConfirmTwoFactorOutput>{
                    success: true,
                    type: this.type,
                    token: response.verificationKey,
                    trustDevice: this.trustDevice,
                });
            }, 800);
        };

        const onError = () => {
            this.isSubmitting = false;
            this.triggerError();
        };

        if (this.type === 'google') {
            this._userSettingsService.verifyGoogleAuthCode(code, this.data.userId, this.trustDevice)
                .pipe(untilDestroyed(this))
                .subscribe({ next: onSuccess, error: onError });
        } else if (this.type === 'sms') {
            this._userSettingsService.verifySmsCode(code, this.data.userId, this.trustDevice)
                .pipe(untilDestroyed(this))
                .subscribe({ next: onSuccess, error: onError });
        }
    }

    ngOnDestroy(): void {
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
    }

    private triggerError() {
        this.hasError = true;
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
            this._otp?.reset();
        }, 600);
    }
}
